/**
 * Minimal rich-text editor for native documents inside the Resources module.
 * Uses contentEditable with execCommand for a tiny, dep-free experience.
 * Stores HTML in text_content. Safe for internal team-only consumption.
 */
import { useEffect, useRef } from "react";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Minus,
  Link as LinkIcon,
  Quote,
} from "lucide-react";

const BTN =
  "w-8 h-8 rounded-md flex items-center justify-center text-[#C9C6C1] hover:bg-white/[0.06] hover:text-white transition-colors";

export default function DocumentEditor({ value, onChange, placeholder, readOnly = false }) {
  const ref = useRef(null);
  const mounted = useRef(false);

  useEffect(() => {
    if (!mounted.current && ref.current) {
      ref.current.innerHTML = value || "";
      mounted.current = true;
    }
  }, [value]);

  const exec = (cmd, arg) => {
    if (readOnly) return;
    document.execCommand(cmd, false, arg);
    if (ref.current) onChange?.(ref.current.innerHTML);
    ref.current?.focus();
  };

  const wrapBlock = (tag) => {
    exec("formatBlock", tag);
  };

  const insertLink = () => {
    const url = window.prompt("Enter URL");
    if (url) exec("createLink", url);
  };

  if (readOnly) {
    return (
      <>
        <div
          className="prose-resource text-[#F4F2EE]"
          dangerouslySetInnerHTML={{ __html: value || "<p class='text-[#8A8783]'>Empty document.</p>" }}
        />
        <style>{PROSE_STYLES}</style>
      </>
    );
  }

  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#000000] overflow-hidden" data-testid="document-editor">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-white/[0.06]">
        <button type="button" className={BTN} onClick={() => wrapBlock("h1")} title="Heading 1" data-testid="doc-h1">
          <Heading1 className="w-4 h-4" />
        </button>
        <button type="button" className={BTN} onClick={() => wrapBlock("h2")} title="Heading 2" data-testid="doc-h2">
          <Heading2 className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-white/[0.08] mx-1" />
        <button type="button" className={BTN} onClick={() => exec("bold")} title="Bold" data-testid="doc-bold">
          <Bold className="w-4 h-4" />
        </button>
        <button type="button" className={BTN} onClick={() => exec("italic")} title="Italic" data-testid="doc-italic">
          <Italic className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-white/[0.08] mx-1" />
        <button type="button" className={BTN} onClick={() => exec("insertUnorderedList")} title="Bullet list">
          <List className="w-4 h-4" />
        </button>
        <button type="button" className={BTN} onClick={() => exec("insertOrderedList")} title="Numbered list">
          <ListOrdered className="w-4 h-4" />
        </button>
        <button type="button" className={BTN} onClick={() => wrapBlock("blockquote")} title="Quote">
          <Quote className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-white/[0.08] mx-1" />
        <button type="button" className={BTN} onClick={insertLink} title="Link">
          <LinkIcon className="w-4 h-4" />
        </button>
        <button type="button" className={BTN} onClick={() => exec("insertHorizontalRule")} title="Divider">
          <Minus className="w-4 h-4" />
        </button>
      </div>

      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange?.(e.currentTarget.innerHTML)}
        className="prose-resource min-h-[340px] max-h-[65vh] overflow-y-auto px-5 py-4 text-[#F4F2EE] leading-relaxed outline-none"
        data-placeholder={placeholder || "Start writing…"}
        data-testid="doc-editor-body"
      />

      <style>{PROSE_STYLES}</style>
    </div>
  );
}

const PROSE_STYLES = `
  .prose-resource h1 { font-size: 1.6rem; font-weight: 600; margin: 1rem 0 0.5rem; }
  .prose-resource h2 { font-size: 1.25rem; font-weight: 600; margin: 0.75rem 0 0.4rem; color: #D5D3CE; }
  .prose-resource p { margin: 0.4rem 0; color: #D5D3CE; }
  .prose-resource ul { list-style: disc; padding-left: 1.25rem; margin: 0.5rem 0; color: #D5D3CE; }
  .prose-resource ol { list-style: decimal; padding-left: 1.25rem; margin: 0.5rem 0; color: #D5D3CE; }
  .prose-resource blockquote { border-left: 2px solid rgba(255,255,255,0.18); padding-left: 0.9rem; color: #C9C6C1; margin: 0.6rem 0; font-style: italic; }
  .prose-resource a { color: #8FA79B; text-decoration: underline; text-underline-offset: 2px; }
  .prose-resource hr { border: none; border-top: 1px solid rgba(255,255,255,0.08); margin: 1rem 0; }
  .prose-resource[data-placeholder]:empty:before { content: attr(data-placeholder); color: #565350; }
`;
